// Pure, framework-free logic for sports and per-sport athlete profiles (see
// `features/teams-sports.md`). A sport's config declares which attribute keys
// a profile may carry and the valid values of each; `validateSportAttributes`
// checks a profile's JSONB `attributes` object against that config. The
// catalog below is the structural source of truth (like the permission
// catalog in `users.ts`); the `teams_sports` migration seeds the same rows.

import type { SportAttributeSpec, SportConfig, SportId } from './types';

/** Every sport currently on the platform, in display order. */
export const SPORT_IDS: readonly SportId[] = ['TABLE_TENNIS', 'FOOTBALL'];

/** Whether a string is a known sport code. */
export function isSportId(value: string): value is SportId {
  return (SPORT_IDS as readonly string[]).includes(value);
}

/** A sport's seed definition, mirrored by the `teams_sports` migration. */
export interface SportSeed {
  id: SportId;
  name: string;
  hasTeams: boolean;
  hasWeightCategory: boolean;
  config: SportConfig;
}

/** The sport catalog: definitions + configs seeded into the `Sport` table. */
export const SPORTS: readonly SportSeed[] = [
  {
    id: 'TABLE_TENNIS',
    name: 'Table tennis',
    hasTeams: true,
    hasWeightCategory: false,
    config: {
      attributes: {
        grip: { kind: 'enum', values: ['SHAKEHAND', 'PENHOLD'] },
        playStyle: { kind: 'enum', values: ['OFFENSIVE', 'ALL_ROUND', 'DEFENSIVE'] },
        formats: { kind: 'multiEnum', values: ['SINGLES', 'DOUBLES', 'MIXED'] },
      },
    },
  },
  {
    id: 'FOOTBALL',
    name: 'Football',
    hasTeams: true,
    hasWeightCategory: false,
    config: {
      attributes: {
        position: {
          kind: 'enum',
          values: ['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'ST'],
        },
      },
    },
  },
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Whether an unknown JSON value (e.g. the `config` JSONB read back from
 * Postgres) is a structurally valid `SportConfig`. Narrows for the server,
 * which must not trust the stored shape blindly.
 */
export function isSportConfig(value: unknown): value is SportConfig {
  if (!isPlainObject(value) || !isPlainObject(value.attributes)) return false;
  return Object.values(value.attributes).every((spec) => {
    if (!isPlainObject(spec)) return false;
    const { kind, values } = spec as Partial<SportAttributeSpec>;
    return (
      (kind === 'enum' || kind === 'multiEnum') &&
      Array.isArray(values) &&
      values.every((v) => typeof v === 'string')
    );
  });
}

/** Per-attribute validation messages; an absent key means that field is valid. */
export type SportAttributeErrors = Record<string, string>;

export interface SportAttributesValidation {
  valid: boolean;
  errors: SportAttributeErrors;
}

/**
 * Validate a profile's `attributes` object against its sport's config. Every
 * declared attribute is optional — a profile must be valid standalone, and an
 * empty object always passes — but a present key must be declared by the
 * config and hold a valid value: for `enum`, one of the declared values; for
 * `multiEnum`, an array of distinct declared values. Undeclared keys are
 * rejected so typos never persist silently.
 */
export function validateSportAttributes(
  config: SportConfig,
  attributes: unknown,
): SportAttributesValidation {
  if (!isPlainObject(attributes)) {
    return { valid: false, errors: { attributes: 'Attributes must be an object.' } };
  }

  const errors: SportAttributeErrors = {};
  for (const [key, value] of Object.entries(attributes)) {
    const spec = config.attributes[key];
    if (!spec) {
      errors[key] = 'Unknown attribute for this sport.';
    } else if (spec.kind === 'enum') {
      if (typeof value !== 'string' || !spec.values.includes(value)) {
        errors[key] = `Must be one of: ${spec.values.join(', ')}.`;
      }
    } else {
      if (
        !Array.isArray(value) ||
        !value.every((v) => typeof v === 'string' && spec.values.includes(v)) ||
        new Set(value).size !== value.length
      ) {
        errors[key] = `Must be a list of distinct values among: ${spec.values.join(', ')}.`;
      }
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}
